import { isEqual } from 'lodash-es';

// Import constants
import { CHARS, DEVICE_ID_KEY } from '../constants/schedule';

// Import types
import type { EventBlock, EventByUser } from '../types/schedule';
import type { SaveRange } from '../types/date';

// Import hooks
import type { BackendRoomPayload } from '../hooks/schedule/useRoomPersistence';

// Import utils
import { logger } from './logger';

export type BackendRoomPayloadWithVersion = BackendRoomPayload & {
  payloadVersion?: number;
};

// Merge and sort overlapping or adjacent segments for the same date
// Sort by date first, then by start index so that same-day segments are
// adjacent during normalization. Sorting only by start index can interleave
// different days (since indices are from 0 to n for every day), which would
// prevent adjacent same-day ranges from merging.
export function normalizeSegments(segments: EventBlock[]): EventBlock[] {
  const list = [...segments].sort((a, b) => {
    const d = a.dateKey.localeCompare(b.dateKey);
    return d !== 0 ? d : a.startIndex - b.startIndex;
  });
  const out: EventBlock[] = [];
  for (const seg of list) {
    const prev = out[out.length - 1];
    if (!prev) {
      out.push({ ...seg });
      continue;
    }
    if (prev.dateKey === seg.dateKey && seg.startIndex <= prev.endIndex) {
      // Overlap or touch (adjacent index). Merge by extending end.
      prev.endIndex = Math.max(prev.endIndex, seg.endIndex);
    } else {
      out.push({ ...seg });
    }
  }
  return out;
}

// Normalize all users' events; optionally stamp a timezone per block
export function normalizeEventsByUser(
  eventsByUser: EventByUser | undefined,
  tz?: string,
): EventByUser {
  const normalized: EventByUser = {};
  Object.keys(eventsByUser || {}).forEach((u) => {
    const list = (eventsByUser as EventByUser)[u] || [];
    const withTz = tz
      ? (list.map((e) => ({ ...e, tz })) as EventBlock[])
      : list;
    normalized[u] = normalizeSegments(withTz);
  });
  return normalized;
}

// Safely parse JSON; return null on failure
export function parseJsonSafe<T = unknown>(json: string | null): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

// Minimal top-level delta between two payload-like objects
export function computeTopLevelDelta(
  base: Partial<BackendRoomPayloadWithVersion> | null,
  next: Partial<BackendRoomPayloadWithVersion>,
): Partial<BackendRoomPayloadWithVersion> {
  if (!base) return { ...next };
  const out: Partial<BackendRoomPayloadWithVersion> = {};
  const keys = new Set<string>([
    ...Object.keys(base),
    ...Object.keys(next),
    'payloadVersion',
    'users',
    'userIdToName',
    'ownerByUserId',
    'eventsByUser',
    'gridStart',
    'gridEnd',
    'savedRange',
    'ownerDeviceId',
    'ownerTimezone',
    'eventTitle',
    'maxParticipants',
  ]);
  keys.forEach((k) => {
    const a = (base as Record<string, unknown>)[k];
    const b = (next as Record<string, unknown>)[k];
    if (!isEqual(a, b)) (out as Record<string, unknown>)[k] = b;
  });
  return out;
}

// Build a stable device id, persisted in localStorage when available
export function buildInitialDeviceId(): string {
  let localId: string;
  try {
    const KEY = DEVICE_ID_KEY;
    let id = localStorage.getItem(KEY);
    if (!id) {
      const chars = CHARS;
      let gen = '';
      for (let i = 0; i < 20; i++)
        gen += chars[Math.floor(Math.random() * chars.length)];
      id = gen;
      localStorage.setItem(KEY, id);
    }
    localId = id;
  } catch (e) {
    logger.error('Failed to initialize device id:', e);
    // Fallback to session-scoped id when localStorage is not available
    localId = 'session_' + Math.random().toString(36).slice(2);
  }
  return localId;
}

// Construct a normalized payload from a RoomSnapshot
export function buildPayloadFromSnapshot(
  snap: {
    users: string[];
    userIdToName: Record<string, string>;
    ownerByUserId: Record<string, string>;
    eventsByUser: EventByUser;
    gridStart: string;
    gridEnd: string;
    savedRange?: SaveRange;
    ownerDeviceId?: string;
    ownerTimezone?: string;
    eventTitle?: string;
    maxParticipants?: number;
  },
  initialTZ: string,
) {
  const tzForRoom = snap.ownerTimezone || initialTZ;
  const normalized = normalizeEventsByUser(snap.eventsByUser, tzForRoom);
  return {
    payloadVersion: 9,
    users: snap.users,
    userIdToName: snap.userIdToName,
    ownerByUserId: snap.ownerByUserId,
    eventsByUser: normalized,
    gridStart: snap.gridStart,
    gridEnd: snap.gridEnd,
    savedRange: snap.savedRange,
    ownerDeviceId: snap.ownerDeviceId,
    ownerTimezone: snap.ownerTimezone,
    eventTitle: snap.eventTitle,
    maxParticipants: snap.maxParticipants,
  };
}

export function isErrorObject(error: unknown, statusCode: number) {
  const nullish = error !== null;
  const isObject = typeof error === 'object';
  const isStatusInError = 'status' in (error as Record<string, unknown>);
  const isStatus = (error as { status?: number }).status === statusCode;

  return nullish && isObject && isStatusInError && isStatus;
}
